import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { fetchDataset, fetchHidden, saveHidden, fetchShifts, saveShifts } from "../lib/api";
import { ShiftCell } from "../components/ShiftCell";
import { ShiftReview } from "../components/ShiftReview";
import { fmtNum, slug } from "../lib/format";
import { monthLabel } from "../lib/csvAnalysis";

const summarise = (raw) => raw.users.map((u) => {
  const keys = Object.keys(u.monthly).sort();
  const visits = keys.reduce((s, k) => s + u.monthly[k][0], 0);
  const qty = keys.reduce((s, k) => s + u.monthly[k][1], 0);
  return { id: u.id, visits, qty, days: Object.keys(raw.days?.[u.id] || {}).length, first: keys[0], last: keys[keys.length - 1] };
}).sort((a, b) => b.visits - a.visits);

const btn = "inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800";

export default function PickersPage() {
  const [rows, setRows] = useState(null);
  const [hidden, setHidden] = useState(new Set());
  const [q, setQ] = useState("");
  const [shifts, setShifts] = useState({});
  const [days, setDays] = useState({});
  useEffect(() => {
    Promise.all([fetchDataset(), fetchHidden(), fetchShifts().catch(() => ({}))]).then(([raw, h, sh]) => { setRows(raw ? summarise(raw) : []); setHidden(new Set(h)); setShifts(sh); setDays(raw?.days || {}); })
      .catch((e) => { toast.error(e.message); setRows([]); });
  }, []);
  const view = useMemo(() => (rows || []).filter((r) => r.id.toLowerCase().includes(q.toLowerCase())), [rows, q]);

  const persist = async (next) => {
    const prev = hidden;
    setHidden(next);
    try { await saveHidden([...next]); } catch (e) { setHidden(prev); toast.error("Could not save"); }
  };
  const toggle = (id) => { const n = new Set(hidden); n.has(id) ? n.delete(id) : n.add(id); persist(n); };
  const setShift = async (id, periods, msg) => {
    const prev = shifts;
    const next = { ...shifts };
    if (periods?.length) next[id] = periods; else delete next[id];
    setShifts(next);
    try { await saveShifts(next); toast.success(msg || (periods?.length ? `${id}: shift history saved` : `${id}: back to auto-detected shifts`)); } catch { setShifts(prev); toast.error("Could not save shift"); }
  };
  const bulk = (show) => { const n = new Set(hidden); view.forEach((r) => (show ? n.delete(r.id) : n.add(r.id))); persist(n); };

  if (!rows) return <div className="flex justify-center pt-40 text-slate-400 gap-3"><Loader2 className="h-5 w-5 animate-spin" /> Loading pickers…</div>;
  if (!rows.length) return (
    <div className="max-w-3xl mx-auto px-6 pt-28" data-testid="pickers-empty">
      <h1 className="font-display text-3xl font-bold text-slate-100">No pickers yet.</h1>
      <Link to="/manage" className="mt-6 inline-flex px-4 py-2.5 rounded-md bg-ep hover:bg-ep-hover text-white text-sm">Upload a CSV</Link>
    </div>
  );
  const shown = rows.filter((r) => !hidden.has(r.id)).length;
  return (
    <div className="max-w-6xl mx-auto px-6 py-12 fade-up" data-testid="pickers-page">
      <p className="label-mono mb-3">Picker view management</p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-100">Choose who appears in the dashboard.</h1>
      <p className="mt-3 text-sm text-slate-400">Unticked pickers are removed from every total, chart, sheet, leaderboard and heatmap. Each picker's shift is auto-detected from their typical first and last pick (e.g. part-time 06:00–10:00); edit it if it's wrong. Changes save instantly and are kept when you upload a new file.</p>
      <ShiftReview days={days} overrides={shifts} hidden={hidden} onSave={setShift} />
      <div className="mt-8 panel">
        <div className="flex flex-wrap items-center gap-3 p-5 border-b border-slate-800">
          <p className="font-mono text-sm text-slate-300 mr-auto" data-testid="pickers-shown-count"><span className="text-emerald-400">{shown}</span> shown · <span className="text-amber-400">{rows.length - shown}</span> hidden</p>
          <div className="relative w-full sm:w-60">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="pickers-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pickers…" className="pl-9 h-9 bg-[#0B0F19] border-slate-700 text-slate-100" />
          </div>
          <button data-testid="pickers-show-all" onClick={() => bulk(true)} className={btn}><Eye className="h-3.5 w-3.5" /> Show {q ? "filtered" : "all"}</button>
          <button data-testid="pickers-hide-all" onClick={() => bulk(false)} className={btn}><EyeOff className="h-3.5 w-3.5" /> Hide {q ? "filtered" : "all"}</button>
        </div>
        <div className="overflow-x-auto max-h-[620px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#111827] z-10"><tr className="border-b border-slate-800">{["Show", "Picker", "Shift (used for lost time)", "Visits", "Qty picked", "Days worked", "Active"].map((h) => <th key={h} className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-slate-500 first:pl-5">{h}</th>)}</tr></thead>
            <tbody>{view.map((r) => {
              const on = !hidden.has(r.id);
              return (
                <tr key={r.id} data-testid={`picker-row-${slug(r.id)}`} onClick={() => toggle(r.id)} className={`border-b border-slate-800/60 last:border-0 cursor-pointer hover:bg-slate-800/40 transition-colors ${on ? "" : "opacity-50"}`}>
                  <td className="pl-5 py-3" onClick={(e) => e.stopPropagation()}><Checkbox data-testid={`picker-visible-${slug(r.id)}`} checked={on} onCheckedChange={() => toggle(r.id)} className="border-slate-600 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500" /></td>
                  <td className="px-4 text-slate-100 font-medium">{r.id}</td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}><ShiftCell id={r.id} userDays={days[r.id]} override={shifts[r.id]} onSave={(p, m) => setShift(r.id, p, m)} /></td>
                  <td className="px-4 font-mono text-slate-300">{fmtNum(r.visits)}</td>
                  <td className="px-4 font-mono text-slate-300">{fmtNum(r.qty)}</td>
                  <td className="px-4 font-mono text-slate-400">{r.days || "—"}</td>
                  <td className="px-4 font-mono text-slate-400 whitespace-nowrap">{monthLabel(r.first)} → {monthLabel(r.last)}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
